// Shared domain types. Mirrors PRD §18 data model.
// Mock-only. No persistence layer.

export type StudioId =
  | 'coburg'
  | 'collingwood'
  | 'frankston'
  | 'windsor'
  | 'central-coast'
  | 'emu-plains'
  | 'liverpool'
  | 'brisbane'
  | 'gold-coast'
  | 'mackay'
  | 'tasmania';

export type Persona =
  | 'georgina'
  | 'studio-manager'
  | 'bookings-agent'
  | 'artist'
  | 'apprentice'
  | 'owner'
  // Back-office money loop (diagram 11). Finance / Accounts runs reconciliation,
  // settlement and day close; the Finance Manager is a narrow signer whose only
  // job is the maker-checker sign-off on what Accounts resolved.
  | 'finance'
  | 'finance-manager';

export type Channel = 'meta-dm' | 'ig-dm' | 'sms' | 'voice' | 'web-form' | 'walk-in' | 'email';

export type DesignType =
  | 'black-and-grey'
  | 'colour-realism'
  | 'traditional'
  | 'neo-traditional'
  | 'fine-line'
  | 'lettering'
  | 'cover-up'
  | 'blackwork'
  | 'sleeve'
  | 'half-sleeve'
  | 'chest-panel'
  | 'back-piece'
  | 'flash';

export type SessionLength = 'small' | 'half-day' | 'full-day' | 'multi-session';

/**
 * Fixed vocabulary of tattoo styles used for customer preferences and the
 * call-brief style picker. Distinct from `DesignType` (which also encodes piece
 * scale like sleeve / back-piece); this list is purely the artistic style.
 */
export type TattooStyle =
  | 'fine-line'
  | 'blackwork'
  | 'colour'
  | 'realism'
  | 'script'
  | 'traditional'
  | 'neo-traditional'
  | 'geometric';

export const TATTOO_STYLES: readonly TattooStyle[] = [
  'fine-line',
  'blackwork',
  'colour',
  'realism',
  'script',
  'traditional',
  'neo-traditional',
  'geometric',
];

export type LeadStage =
  | 'new'
  | 'qualifying'
  | 'deposit-pending'
  | 'booked'
  | 'completed'
  | 'lapsed'
  | 'dormant'
  | 'do-not-contact';

export type Tone = 'casual-young' | 'casual-older' | 'professional' | 'warm-familiar' | 'reserved';

export type EngagementStatus = 'active' | 'warm' | 'lapsed' | 'dormant';

export interface ConversationEntry {
  id: string;
  customerId: string;
  channel: Channel;
  direction: 'in' | 'out';
  body: string;
  timestamp: string; // ISO
  authorRole: 'customer' | 'agent' | 'artist' | 'system';
  authorName?: string;
  /** Resolved bookings-agent id for this entry (Prompt F per-agent call reporting). */
  agentId?: string;
  attachments?: Array<{
    kind: 'image' | 'audio' | 'file';
    url: string;
    durationSec?: number;
    transcript?: string;
  }>;
  futureReadinessSignal?: {
    triggerDate: string;
    rawPhrase: string;
  };
  // Grouped call transcript: when present, the timeline entry renders the full
  // back-and-forth in a single card instead of one card per utterance.
  transcript?: Array<{
    speaker: 'customer' | 'agent';
    body: string;
    atIso: string;
  }>;
  // When set, this entry represents a Stripe deposit link being sent. The
  // timeline renders a lifecycle card (Sent / Viewed / Paid / Expired) driven
  // by the live Deposit record in the brain store.
  depositId?: string;
  // When set, this entry represents a waiver link being sent. The timeline
  // renders a lifecycle card (Sent / Signed / Expired) driven by the live
  // Waiver record in the brain store.
  waiverId?: string;
}

export interface FutureReadinessSignal {
  id: string;
  customerId: string;
  rawPhrase: string;
  scheduledFor: string; // ISO
  recommendedChannel: Channel;
  status: 'scheduled' | 'fired' | 'cancelled' | 'snoozed';
  createdAt: string;
  draftMessage?: string;
}

export interface DesignIntent {
  type: DesignType;
  placement: string;
  estimatedHours: number;
  size: SessionLength;
  notes?: string;
  referencePhotos: string[];
  /**
   * Working booking-brief fields, filled in the chat page's left rail before a
   * booking exists. They persist here so the rail survives navigation and flow
   * onto the held draft booking when the deposit is sent.
   */
  description?: string;
  colour?: 'colour' | 'black-and-grey';
  collabArtistIds?: string[];
  pieceMode?: 'continue' | 'new' | 'one-off';
  continuePieceId?: string;
  newPieceTitle?: string;
  estimatedSessions?: number;
  /** Structured placement (mirrors the Booking fields), set via PlacementFields. */
  bodyPart?: BodyPart;
  bodyPartSide?: BodyPartSide;
  bodyPartQualifier?: BodyPartQualifier;
}

/**
 * Structured medical intake. Any populated clinical field makes the profile
 * "non-empty", which raises a derived `medical-note` flag on the customer's
 * bookings. `lastUpdatedAt` / `lastUpdatedBy` are stamped by the store on save.
 */
export interface MedicalProfile {
  allergies?: string[];
  medications?: string[];
  conditions?: string[]; // e.g. 'diabetic', 'pregnant', 'blood-thinner'
  notes?: string;
  lastUpdatedAt: string;
  lastUpdatedBy: string;
}

export interface CustomerRecord {
  id: string;
  firstName: string;
  lastName: string;
  preferredName?: string;
  /** Allergies / conditions / meds captured at intake. Drives the medical-note flag. */
  medicalProfile?: MedicalProfile;
  phone: string;
  /**
   * Set true once a real phone has been confirmed at a deposit payment (Diagram
   * 02: phone = SMS channel + "number on file"). Agent-sourced records already
   * carry a phone; this marks it verified through a payment interaction.
   */
  numberOnFile?: boolean;
  email?: string;
  instagramHandle?: string;
  dateOfBirth?: string;
  ageBand: '18-24' | '25-34' | '35-44' | '45-54' | '55+';
  /** Street address - optional; captured/confirmed at waiver signing. */
  address?: string;
  suburb: string;
  postcode: string;
  state: string;
  distanceToNearestStudioKm: number;
  nearestStudioId: StudioId;
  primaryStudioId: StudioId;
  source: {
    channel: Channel;
    campaignId?: string;
    firstTouchAt: string;
    acquisitionCostAud?: number;
  };
  leadScore: number; // 0-100
  leadStage: LeadStage;
  toneTag: Tone;
  engagementStatus: EngagementStatus;
  conversation: ConversationEntry[];
  designIntent?: DesignIntent;
  /** Preferred artist, captured on a call; later scopes artist filtering. */
  preferredArtistId?: string;
  /** Preferred style, captured on a call; later scopes artist filtering. */
  preferredStyle?: TattooStyle;
  /** Preferred studio, captured on a call; later scopes artist filtering. */
  preferredStudioId?: StudioId;
  rapportNotes: Array<{ id: string; note: string; author: string; timestamp: string }>;
  futureReadinessSignals: FutureReadinessSignal[];
  bookings: string[]; // booking ids
  pieces: string[]; // piece ids
  deposits: string[]; // deposit ids
  waivers?: string[]; // waiver ids
  creditOnFile: CreditOnFile;
  crossStudioTouches: StudioId[];
  doNotContact: boolean;
  lifetimeSpendAud: number;
  hero?: boolean;
}

export interface CreditOnFile {
  balanceAud: number;
  expiryDate?: string;
  history: Array<{
    id: string;
    type: 'added' | 'applied' | 'refunded' | 'expired';
    amountAud: number;
    bookingId?: string;
    note: string;
    timestamp: string;
  }>;
}

/**
 * What happened in the in-chair rebook conversation at the end of a session.
 * Captured on the COMPLETED booking so the leaderboard + reporting can attribute
 * rebooks (or declines) to the artist / manager / sales-team who facilitated.
 *
 * Outcome A - customer locks the next session at the chair; deposit on file
 * rolls forward to cover that new booking; today's session is paid IN FULL
 * (deposit isn't consumed today, it's value held for the future booking).
 *
 * Outcome B - customer declines a future booking; deposit on file is applied
 * to today's payment so they walk out paying session price minus deposit.
 *
 * Outcome C - customer is undecided; sales agent follows up via a queue task.
 * `resolvedOutcome` populates when the agent records the resolution.
 */
export type RebookOutcome =
  | {
      kind: 'rebookedInChair';
      newBookingId: string;
      depositRolledForward: boolean;
    }
  | {
      /**
       * Client is coming back but won't book a slot on the spot. The deposit
       * rolls forward as a held pot (paid-but-not-booked) to protect the future
       * booking; no next booking is created. Sits between lock-now and defer-to-sales.
       */
      kind: 'willRebookLater';
      depositRolledForward: boolean;
      /**
       * L3 (17 Jul): free-text reason (was a chip). The manager must type why
       * they'll rebook later; it flows onto the sales follow-up task note, since
       * "will rebook later" IS the deferral now. `reason` kept for old records.
       */
      reason?: RebookLaterReason | string;
      note?: string;
    }
  | {
      kind: 'noRebookDepositApplied';
      /** L3 (17 Jul): mandatory free-text reason (was a required chip). */
      reason: RebookDeclineReason | string;
      reasonNote?: string;
    }
  | {
      kind: 'rebookDeferredToSales';
      salesTaskId: string;
      /** Why it went to sales (structured chip) — alongside the free-text note. */
      reason?: RebookDeferReason;
      resolvedAt?: string;
      resolvedOutcome?: 'booked' | 'declined' | 'noContact';
    };

export type RebookDeclineReason =
  | 'not-sure-on-design'
  | 'going-overseas'
  | 'moving-studios'
  | 'needs-to-think'
  | 'other';

export type RebookDeferReason =
  | 'wants-bigger-piece'
  | 'checking-calendar'
  | 'deciding-design'
  | 'budget-timing'
  | 'other';

/**
 * Why a client will rebook but not on the spot (K4). A small preset list the
 * client can rename. `// ASSUMPTION:` reasons per the runbook - client to confirm.
 */
export type RebookLaterReason =
  | 'needs-to-check-diary'
  | 'money'
  | 'undecided-design'
  | 'other';

export interface RebookAttribution {
  facilitatedBy: 'artist' | 'floorManager' | 'salesTeam' | 'otherStaff';
  /** Stable id from artists / agents records. */
  facilitatedByStaffId: string;
  capturedAt: string;
}

export type RebookPieceSize = 'large' | 'small';

/**
 * The artist's lightweight, in-chair read on the rebook: will the customer come
 * back, and how big is the next piece. It PRE-FILLS the studio manager's
 * rebook-outcome panel at check-out but is NOT the system of record - the
 * manager confirms or overrides the outcome (and owns decline reasons +
 * payments) at the make-sale. Captured by the artist from their dashboard.
 */
export interface ArtistRebookIntent {
  willRebook: boolean;
  pieceSize: RebookPieceSize;
  /** The artist who left the read (booking.artistId at capture time). */
  artistId: string;
  capturedAt: string;
}

export interface RebookFollowupTask {
  id: string;
  customerId: string;
  studioId: StudioId;
  sourceBookingId: string;
  originalArtistId: string;
  /** Free-text note from the floor team about how the conversation went. */
  note?: string;
  createdAt: string;
  /** Set when the bookings agent records the resolution. */
  resolvedAt?: string;
  resolvedOutcome?: 'booked' | 'declined' | 'noContact';
}

/**
 * Healing-check task (Prompt G, 09 Jul). Minted at checkout for a completed
 * session that did NOT rebook in the chair, due 7 days post-session. Surfaces as
 * a `healing-check` queue item ("how's the tattoo healing?"), money cohort when a
 * deposit still sits on the piece.
 */
export interface HealingCheckTask {
  id: string;
  customerId: string;
  studioId: StudioId;
  sourceBookingId: string;
  pieceId?: string;
  /** A deposit still on the piece, if any — makes the queue item money-cohort. */
  depositId?: string;
  createdAt: string; // session completion time
  dueAt: string; // createdAt + 7 days
  resolvedAt?: string;
}

/**
 * No-rebook mid-piece Ops flag (tv-3gl). Emitted at checkout when a client
 * finishes a sitting but is still mid-piece (unfinished) and does NOT rebook (a
 * rebook needs no reason, the deposit just rolls, but not rebooking mid-piece is
 * the leak, so it is made accountable). Carries the MANDATORY typed reason
 * (`reason`, from the RebookOutcome) so Ops can chase the unfinished work.
 *
 * This is STATE ONLY: tv-3gl emits it; the Ops worklist that reads it is a
 * separate track (tv-v4p / diagram 09 `i7`). Consumers must not mutate it beyond
 * stamping `resolvedAt`. Shape agreed with tv-v4p: do not add fields here without
 * coordinating, so the Ops feeder can read it unchanged.
 */
export interface NoRebookFlag {
  id: string;
  /** The completed booking whose no-rebook mid-piece outcome raised the flag. */
  sourceBookingId: string;
  customerId: string;
  studioId: StudioId;
  /** The unfinished piece left open by the no-rebook. */
  pieceId: string;
  /** Mandatory typed reason captured on the decline path (RebookOutcome.reason). */
  reason: string;
  createdAt: string; // checkout time
  /** Set by Ops (tv-v4p) once the unfinished work has been chased/closed. */
  resolvedAt?: string;
}

/**
 * Declined no-deposit override (diagrams 05/09). Ops declining a no-deposit override
 * sets this flag: the agent can no longer create a no-money booking for the customer
 * (no second trip through Ops) and must chase an actual deposit. Resolves when a
 * deposit is paid. STATE ONLY - Track A2 (Ops decline) writes it via
 * flagNoDepositDecline; Track C2 (agent queue) reads it to block a no-money rebook
 * and stamps resolvedAt via resolveNoDepositDecline once a deposit lands. Do not add
 * fields without coordinating both tracks.
 */
export interface NoDepositDeclineFlag {
  id: string; // `ndf-${customerId}` - one active flag per customer
  customerId: string;
  /** The booking / override the decline was raised against, if any. */
  sourceBookingId?: string;
  /** Ops's typed decline reason (optional but usually present). */
  reason?: string;
  /** Persona who declined (Ops role). */
  by: string;
  createdAt: string;
  /** Set once a deposit is paid (C2 / deposit-paid path). */
  resolvedAt?: string;
}

// --- Structured booking fields (decomposed from the old freeform notes box) ---
// The real studio notes today are a single box stuffed with ~10 recurring data
// types; these enums pull the recurring ones into first-class, queryable fields.

// Ordered head-down rather than alphabetically, so the picker scans like a body.
// Anything not listed goes in the free-text `placement` field behind the
// placement picker's "Other" pill.
export type BodyPart =
  | 'head'
  | 'face'
  | 'ear'
  | 'neck'
  | 'chest'
  | 'back'
  | 'shoulder'
  | 'upper-arm'
  | 'bicep'
  | 'forearm'
  | 'hand'
  | 'torso'
  | 'hip'
  | 'buttocks'
  | 'thigh'
  | 'hamstring'
  | 'shin'
  | 'calf'
  | 'foot';

export type BodyPartSide = 'left' | 'right';
export type BodyPartQualifier = 'inner' | 'outer' | 'upper' | 'lower';

/** Where the reference imagery actually lives (agents record this constantly). */
export type RefsLocation =
  | 'uploaded'
  | 'on-client-card'
  | 'with-artist'
  | 'ig-dms'
  | 'physical-folder';

/**
 * Operational flags surfaced against a booking - purely *derived facts* about the
 * booking, computed at render time (see `computeBookingFlags`). Checkout-time
 * actions (rebook, deposit rollover, balance) live in the floor checkout flow
 * (`sessionFlow.ts`), not here.
 */
export type BookingFlag =
  | 'medical-note'
  | 'deposit-topup-pending'
  | 'late-fill';

/**
 * Customer-level flags - derived facts about a customer (not persisted),
 * computed at render time by `deriveCustomerFlags` from the customer's latest
 * signed waiver answers + their medical profile. Surface anywhere the customer
 * appears (record header, booking card, check-in).
 */
export type CustomerFlag =
  | 'waiver-signed'
  | 'medical-condition'
  | 'allergy'
  | 'under-medication'
  | 'photo-opt-out';

export type InvoiceStatus =
  | 'not-issued'
  | 'draft'
  | 'issued'
  | 'paid'
  | 'uninvoiced-writeoff';

/**
 * Why a booking's day rate deviates from the artist's standard. `standard` means
 * no deviation; the others explain the reason for the custom rate (a gap-fill
 * discount, friends-and-family / collab / apprentice pricing, …) so revenue
 * given up stays reportable.
 */
export type DayRateBasis =
  | 'standard'
  | 'gap-fill'
  | 'friends-family'
  | 'collab'
  | 'apprentice'
  | 'other';

/** Position in the confirmation cadence (7-day text → call → 2-day text → confirmed). */
export type ConfirmationStage =
  | 'pre-7d'
  | '7d-text-sent'
  | '7d-call-pending'
  | '7d-call-done'
  | '2d-text-sent'
  | 'confirmed'
  | 'unreachable';

export interface ConfirmationTouch {
  at: string; // ISO
  channel: 'sms' | 'call' | 'ig' | 'auto-studio';
  outcome: 'confirmed' | 'no-response' | 'left-vm' | 'rescheduled';
  by: string;
}

/** Why a booking was moved. Structured so fill/move history is reportable. */
export type RescheduleReason =
  | 'gap-fill'
  | 'artist-move'
  | 'client-move'
  | 'cancellation-fill'
  | 'other';

/** How a booking was created / where the lead came in. */
export type BookingSource =
  | 'agent-initiated-call'
  | 'agent-initiated-chat'
  | 'self-book-link'
  | 'artist-link'
  | 'in-chair-rebooking'
  | 'reactivation-campaign'
  | 'walk-in'
  // Widened channel-level sources (real intake variants agents record).
  | 'phone-inbound'
  | 'phone-outbound'
  | 'ig-dm'
  | 'fb-dm'
  | 'web-form'
  | 'in-studio-walkin'
  | 'in-studio-rebook'
  | 'artist-referral';

export interface Booking {
  id: string;
  customerId: string;
  studioId: StudioId;
  artistId: string;
  pieceId?: string;
  start: string; // ISO
  durationMin: number;
  sessionLength: SessionLength;
  status:
    | 'held'
    | 'pending-deposit'
    | 'confirmed'
    | 'checked-in'
    | 'in-chair'
    | 'rescheduled'
    | 'completed'
    | 'cancelled'
    | 'no-show'
    // Kept-original marker (15 Jul ruling): the prior record after a reschedule.
    // Preserved for calendar history; excluded from occupancy/gap/queue maths.
    | 'superseded';
  depositId?: string;
  /**
   * True on a `held` ghost booking for a THIS-WEEK dated agent link (tv-nid): the
   * near slot may be double-held, and whichever client pays first locks it while
   * the losing hold is voided and its client re-called (routeOverbookRecall). Set
   * only for this-week agent links; never for out-of-week holds or firm Sweeper
   * gap-fill dated-links (which keep a 24h lock with no overbook).
   */
  overbookable?: boolean;
  /**
   * Client asked to be brought forward if an earlier slot opens (checkbox set at
   * booking by anyone who books). The Sweeper's bring-forward rung sorts these to
   * the top when filling a gap (bd tv-jqv, board 08).
   */
  preferBroughtForward?: boolean;
  /**
   * Structured design fields (decomposed from the old single notes box).
   * `designBrief` is now a derived/AI-summary string built from these.
   */
  placement?: string;
  colour?: 'colour' | 'black-and-grey';
  styleTags?: string[];
  /** Structured placement (supersedes the free-text `placement` when present). */
  bodyPart?: BodyPart;
  bodyPartSide?: BodyPartSide;
  bodyPartQualifier?: BodyPartQualifier;
  /**
   * Legacy fallback. Now a derived/AI-summary string built from the structured
   * fields; only rendered when `aiSummary` and the structured fields are absent.
   */
  designBrief: string;
  /** One-line AI/agent summary of the brief (supersedes `designBrief` in the UI). */
  aiSummary?: string;
  referencePhotos: string[];
  /** Where the reference imagery physically lives. */
  refsLocation?: RefsLocation;
  /** Day rate quoted for the session (from a tier card or built from hours). */
  dayRateAud?: number;
  /** Why the day rate deviates from the artist's standard (defaults to `standard`). */
  dayRateBasis?: DayRateBasis;
  /**
   * Suggestions the agent has explicitly dismissed. Operational flags are no
   * longer stored - they're derived at render time by `computeBookingFlags`.
   * Only the *dismissed* suggestions persist, so a dismissed nudge stays hidden.
   */
  dismissedFlagSuggestions?: BookingFlag[];
  /** Other artists collaborating on the piece for this session. */
  collabArtistIds?: string[];
  /** Invoicing state, tracked independently of the deposit ledger. */
  invoiceStatus?: InvoiceStatus;
  /** Confirmation-cadence position + the touches that got us there. */
  confirmationStage?: ConfirmationStage;
  confirmationTouches?: ConfirmationTouch[];
  /** When the customer actually started (vs the scheduled `start`). */
  actualStart?: string;
  source: BookingSource;
  createdAt: string;
  notes?: string;
  inChairRebooking?: boolean;
  /** When the client was checked in at the desk (status → 'checked-in'). */
  checkedInAt?: string;
  /** When the client was seated in the chair (status → 'in-chair'). Distinct from check-in. */
  seatedAt?: string;
  checkedOutAt?: string;
  sessionNotes?: string;
  amountPaidAud?: number;
  paymentMethod?: 'card' | 'cash' | 'rolled-deposit' | 'mixed';
  paidAt?: string;
  rolledDepositId?: string;
  boothNumber?: number;
  /** Rebook captured at this session's checkout. Populated on completed sessions. */
  rebookOutcome?: RebookOutcome;
  /** Who facilitated the rebook conversation. Always present alongside rebookOutcome. */
  rebookAttribution?: RebookAttribution;
  /**
   * Artist's lightweight in-chair read, captured before check-out. Pre-fills the
   * manager's rebook-outcome panel - it is a hint, not the system of record.
   */
  artistRebookIntent?: ArtistRebookIntent;
  /** When this booking was created from a previous session's rebook (Outcome A). */
  sourceBookingId?: string;
  /**
   * Move-with-history (never delete). Appended each time the booking is dragged
   * to a new slot/artist in the calendar. Surfaced as a tooltip + in the detail card.
   */
  rescheduleHistory?: Array<{
    fromIso: string;
    toIso: string;
    reason?: RescheduleReason;
    /** Free-text note captured for the reason (required when reason is 'other'). */
    note?: string;
    by: string;
    at: string;
  }>;
  /**
   * Keep-original reschedule links (15 Jul ruling). Set on the ORIGINAL
   * (now `superseded`) record: the new booking that replaced it.
   */
  rescheduledToBookingId?: string;
  /** Set on the NEW record: the original booking it was rescheduled from. */
  rescheduledFromBookingId?: string;
  /** Set when the customer confirms their slot in the confirmation cadence. */
  confirmedAt?: string;
  /**
   * Set when a Sweeper gap-fill was verbally confirmed on the call at booking
   * time. Such a booking bypasses the pre-appointment confirmations cadence
   * entirely (the confirmation message + waiver still fire on confirm); the
   * confirmations worklist excludes it (bd tv-pt8, board 08).
   */
  confirmedOnCall?: boolean;
  /** Persisted count of confirmation call sequences attempted without reaching/confirming (0..3).
   *  Drives the paired-day cadence + the 3rd-sequence Ops escalation. */
  confirmationAttempts?: number;
  /** ISO set when the 3rd confirmation sequence goes unanswered and the booking is
   *  flagged to Ops (the escalation the Ops worklist consumes). Set once, then stable. */
  opsEscalatedAt?: string;
  /** ISO Ops asked the artist to personally reach out to the client (tv-var escalation chain). */
  artistAskedAt?: string;
  /** ISO deadline Ops set for the artist to get a response before Ops cancels. */
  artistAskDeadlineIso?: string;
  /**
   * True when this booking was confirmed with NO deposit - either waived
   * directly by Georgina/the owner, or an agent's no-deposit override request
   * that was approved. The slot is held/confirmed without money down; the
   * customer pays in full on the day. See OverrideRequest + src/domain/approvals.ts.
   */
  depositWaived?: boolean;
  /**
   * The bookings-agent who booked this session. Mirrors Deposit.takenByAgentId
   * (per-agent attribution). Stamped from the session operator at creation;
   * carried across a keep-original reschedule. Seeded on fixtures is an
   * // ASSUMPTION: pending real data.
   */
  bookedByAgentId?: string;
}

export type OverrideRequestStatus = 'pending' | 'approved' | 'rejected';

/**
 * A request to book a slot with NO deposit (below the studio minimum). Raised by
 * a non-privileged persona from the booking form when they set the deposit to
 * zero; the booking is created `held` and this request lands in Georgina's
 * Approvals queue. Georgina/Owner approve (booking → `confirmed`, depositWaived)
 * or reject (booking stays `held` for follow-up). Privileged personas who waive
 * a deposit directly also record one of these, pre-resolved to `approved`, so
 * every no-deposit booking has an audit trail. See src/domain/approvals.ts.
 */
export interface OverrideRequest {
  id: string;
  bookingId: string;
  /** Persona (or staff label) who raised the request. */
  requestedBy: string;
  /** Mandatory reason note, e.g. "trusted repeat customer, paying full on the day". */
  note: string;
  status: OverrideRequestStatus;
  createdAt: string;
  /** Persona (or staff label) who approved/rejected. Set on resolution. */
  resolvedBy?: string;
  resolvedAt?: string;
  /** Optional note the resolver leaves when approving/rejecting. */
  resolutionNote?: string;
}

/** A single movement on a deposit's ledger (paid, top-up, rollover, forfeit, …). */
export interface DepositLedgerEntry {
  at: string; // ISO
  amountAud: number;
  kind:
    | 'paid'
    | 'topup'
    | 'rollover-in'
    | 'rollover-out'
    | 'forfeit'
    // Management reversal of a forfeit (G2): returns the money to "paid".
    | 'reinstate'
    // Deposit consumed when the piece completes (Diagram 02): an explicit
    // "used up" movement, distinct from the reporting-side derivation.
    | 'redeemed'
    | 'refunded'
    | 'transfer-in'
    | 'transfer-out';
  by?: string;
  note?: string;
  /** Where this movement's money went: card/transfer/link settle to the bank, cash sits in the till. */
  channel?: DepositChannel;
  /** Target references (G2), so a movement records where the money went / came from. */
  toBookingId?: string;
  fromBookingId?: string;
  toPieceId?: string;
  /** The other deposit in a rollover/transfer pair — the link between both records. */
  counterpartDepositId?: string;
}

export interface Deposit {
  id: string;
  customerId: string;
  bookingId?: string;
  /** When set, this deposit follows a piece across its sessions (not one booking). */
  pieceId?: string;
  studioId: StudioId;
  artistId?: string;
  amountAud: number;
  /**
   * How much has actually been paid so far. Enables a "partial" state.
   * When absent on a `paid` deposit, treat as fully paid (=== amountAud).
   */
  amountPaid?: number;
  feesAud: number;
  netAud: number;
  status: 'pending' | 'paid' | 'expired' | 'refunded' | 'rolled-over';
  channelSent: Channel;
  /**
   * Reconciliation code carried from the self-book link this deposit was taken
   * through (e.g. AL-ASHER-4F7K). Present for artist-link deposits.
   */
  depositReference?: string;
  stripePaymentLinkId: string;
  stripeCheckoutSessionId?: string;
  expiresAt: string;
  createdAt: string;
  viewedAt?: string;
  paidAt?: string;
  /** Set when an expired deposit link is followed up / resent (C8). Non-destructive. */
  resentAt?: string;
  /** Set when an automated top-up reminder is sent (K2). Drives the reminder cadence. */
  lastTopupReminderAt?: string;
  policy: 'seven-day-rule' | 'no-refund' | 'custom';
  source: Booking['source'] | 'reactivation-campaign' | 'confirmation-rescheduled';
  /** Set for manually-logged deposits (EFTPOS / cash / bank transfer in person). */
  method?: 'eftpos' | 'cash' | 'bank-transfer';
  /**
   * The bookings-agent who secured this deposit (Prompt F per-agent reporting).
   * Stamped from the current session operator at the deposit-creation site.
   * Seeded attribution on fixtures is an `// ASSUMPTION:` pending real data.
   */
  takenByAgentId?: string;
  /**
   * Full money-movement history. When present, the booking's "deposit paid" is
   * derived from this (paid + topup + rollover-in − rollover-out − forfeit).
   */
  ledger?: DepositLedgerEntry[];
  /** Customers this deposit is split across (e.g. couple/friends booking together). */
  splitWithCustomerIds?: string[];
  /**
   * Slot discussed on the call and pinned before sending the deposit. Grounds the
   * later booking so it lands with the right artist/time. Durable source of truth.
   */
  discussedSlot?: { artistId: string; startIso: string; endIso: string };
  /** Human-readable piece name captured on the call (e.g. "Jesus back piece"). */
  pieceName?: string;
  /**
   * The id of the UNPAID held placeholder booking created when the deposit link
   * was sent. Distinct from `bookingId`, which is set only once the deposit is
   * paid and the booking locked (confirmed). D6 in the Phase 0 spec.
   */
  heldBookingId?: string;
  /**
   * Set on an EXPIRED deposit when a replacement link is sent for it: the id of
   * the fresh deposit. Marks this record "link resent" while retaining it.
   */
  supersededByDepositId?: string;
}

export type TopupInstalmentStatus = 'scheduled' | 'reminded' | 'paid' | 'missed' | 'cancelled';

export interface TopupInstalment {
  /** ISO instant this instalment is due. */
  dueIso: string;
  /** Whole AUD dollars owed on this instalment. */
  amountAud: number;
  status: TopupInstalmentStatus;
  /** ISO when the reminder for this instalment fired (reconciler stamp). */
  remindedAt?: string;
  /** ISO when this instalment was paid. */
  paidAt?: string;
}

export type TopupPlanStatus = 'active' | 'completed' | 'cancelled';

/** Recurring cadence the builder offers: pay every 7 or every 14 days. */
export type TopupFrequency = 'weekly' | 'fortnightly';

/** An agent-built pay-over-time plan for a deposit. Deterministic + sim-clock driven;
 *  the scheduler (tv-opw) reconciles instalments against the sim clock. The builder
 *  picks a start date and a cadence (weekly/fortnightly), NOT freeform dates; the
 *  `instalments` array is the source of truth, `frequency`/`startIso` make the plan
 *  self-describing for the builder + summary. */
export interface TopupPlan {
  id: string; // e.g. `topup-<depositId>`
  depositId: string; // the Deposit being paid over time
  customerId: string;
  bookingId?: string; // the held/real booking the plan secures
  studioId: StudioId;
  /** Full deposit target in whole AUD dollars. */
  totalAud: number;
  /** Recurring cadence chosen in the builder. */
  frequency: TopupFrequency;
  /** ISO due date of the first instalment. */
  startIso: string;
  instalments: TopupInstalment[];
  status: TopupPlanStatus;
  /** Count of missed-instalment chase sequences attempted without reaching the client (0..3). */
  chaseAttempts?: number;
  createdAt: string; // ISO
  createdByAgentId?: string;
}

export type WaiverStatus = 'not-sent' | 'sent' | 'signed' | 'expired';

export type WaiverChannel = 'sms' | 'email' | 'manual';

export type WaiverFoundVia = 'facebook' | 'instagram' | 'walk-by' | 'friend';

/** Identity confirmed at signing (mirrors the paper form's name/DOB/contact block). */
export interface WaiverSignedIdentity {
  fullName: string;
  dateOfBirth?: string;
  address?: string;
  phone?: string;
  email?: string;
}

/**
 * What the customer actually agreed to when signing, modelled on the printed
 * Addikted to Ink waiver (see docs/addicted_to_ink_waiver.txt). The three
 * clinical arrays are populated from the medical-declaration checklist and drive
 * the customer-level flags (allergy / medical-condition / under-medication) via
 * `deriveCustomerFlags`. `ageConfirmed` + `consent` gate a valid signature.
 */
export interface WaiverAnswers {
  allergies: string[];
  /** Declared conditions (from the negative-declaration checklist + free text). */
  conditions: string[];
  /** Declared medications (e.g. blood thinners) from the checklist. */
  medications: string[];
  /** Customer confirmed they are 18+. */
  ageConfirmed: boolean;
  /** Customer read and agreed to all acknowledgement clauses. */
  consent: boolean;
  /** Ids of the acknowledgement clauses agreed to (see WAIVER_DOCUMENT.clauses). */
  acknowledgedClauseIds?: string[];
  /** Photo release is opt-OUT: true (default) = happy to be photographed. */
  photoConsent?: boolean;
  /** "How did you find us?" marketing attribution. */
  foundVia?: WaiverFoundVia;
  /** Typed full-name signature. */
  signatureName?: string;
  /** Identity confirmed at signing. */
  signedIdentity?: WaiverSignedIdentity;
}

/**
 * A medical-intake / consent waiver for a booking. One waiver per booking;
 * looked up as "latest for a booking / customer". `answers` populate on signing.
 */
export interface Waiver {
  id: string;
  customerId: string;
  bookingId: string;
  status: WaiverStatus;
  /** How the waiver link was sent (or 'manual' when signed at the desk). */
  sendChannel: WaiverChannel;
  sentAt?: string; // ISO
  signedAt?: string; // ISO
  createdAt: string; // ISO
  answers?: WaiverAnswers;
  /** Version of the agreement text that was signed (WAIVER_DOCUMENT.version). */
  documentVersion?: string;
}

export interface Piece {
  id: string;
  customerId: string;
  /** Human-readable label, e.g. "Jesus back piece". Falls back to style/type in UI. */
  title?: string;
  designType: DesignType;
  placement: string;
  style: string;
  referencePhotos: string[];
  assignedArtistId: string;
  estimatedTotalHours: number;
  hoursCompleted: number;
  estimatedTotalSessions?: number;
  sessionsCompleted?: number;
  status:
    | 'planning'
    | 'in-progress'
    | 'completed'
    | 'abandoned'
    | 'paused-will-return'
    | 'abandoned-large'
    | 'single-piece-done';
  bookingsLinked: string[];
  lastSessionDate?: string;
  nextPlannedSession?: string;
  abandonedReason?: string;
  /** Per-session pricing expectations, indexed by session number (1-based). */
  pricingPlan?: Array<{
    sessionIndex: number;
    expectedDayRate: number;
    sessionKind: 'start' | 'continuation' | 'final' | 'touchup';
  }>;
  /** Session-specific reference photos, keyed by bookingId, stacked on top of `referencePhotos`. */
  sessionRefs?: Record<string, string[]>;
  progressPhotos?: Array<{
    id: string;
    url: string;
    capturedAt: string;
    bookingId?: string;
    caption?: string;
    /**
     * What the artist shot it as. A progress photo is the work itself; a
     * reference is what they were working FROM (stencil, printout, the client's
     * own picture). Absent reads as 'progress' - that's what every photo on the
     * piece meant before the distinction existed.
     */
    kind?: SessionPhotoKind;
  }>;
}

/** Progress photo vs the reference it was worked from. Tagged at capture. */
export type SessionPhotoKind = 'progress' | 'reference';

export interface LeaveBlock {
  id: string;          // stable unique id (crypto.randomUUID() in the UI)
  startDate: string;   // ISO date-time
  endDate: string;     // ISO date-time; single day === same calendar day as startDate
  type: 'rdo' | 'comp' | 'flex-swap' | 'time-off' | 'annual';
  note?: string;
  confirmedBy?: string;
  /** For type 'comp': the yyyy-MM-dd of the double day this comp day compensates. */
  compensatesDate?: string;
}

export interface ArtistAvailability {
  pattern: Record<DayOfWeek, { startTime: string; endTime: string } | null>;
  rdoDates: string[];
  leaveBlocks: LeaveBlock[];
  perDayCapacity: number;
  /**
   * Opt-in to late (15:00) starts that would run past the day's pattern end.
   * Artists without it do not offer a 15:00 start where a full day would spill
   * past their pattern window; a half day at 15:00 is still fine when it fits.
   * See the universal self-book flow (src/domain/selfBookFlow.ts).
   */
  nightSessions?: boolean;
  /**
   * Per-date SECOND shift (a two-shift / ~16h day), distinct from the
   * consecutive-days "double day" in workload.ts. Keyed yyyy-MM-dd. When present
   * for a day, the effective shift end (payoutFlow.resolveShiftEnd) extends to
   * the end of this second window. The recurring `pattern` stays one window/day.
   */
  secondShiftDates?: Record<string, { startTime: string; endTime: string }>;
}

export type DayOfWeek = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

export interface ArtistRates {
  hourlyAud: number;
  smallAud: number;
  halfDayAud: number;
  fullDayAud: number;
}

export interface Artist {
  id: string;
  firstName: string;
  lastName: string;
  displayName: string;
  studioId: StudioId;
  instagramHandle: string;
  styleSpeciality: DesignType[];
  bio: string;
  avatarUrl?: string;
  isApprentice: boolean;
  commissionRate: number; // 0-1
  /**
   * How this artist is paid out of a day's takings (Prompt E). Absent === the
   * percentage default derived from `commissionRate`. Owner-artists keep 100%;
   * apprentices are typically a flat day fee. Resolved via
   * `arrangementForArtist` in `commissionEngine.ts` — never read raw.
   */
  commissionArrangement?: CommissionArrangement;
  availability: ArtistAvailability;
  joinedAt: string;
  rates: ArtistRates;
  email?: string;
  phone?: string;
  archived?: boolean; // soft-delete flag; absent/false === active
  /**
   * Annual paid-leave allowance in days (L7, 17 Jul). Absent === no allowance set.
   * Consumed by allowance-consuming leave (annual / time-off / flex-swap); RDO and
   * comp days do NOT consume it. See `allowanceConsumingDays` in `leaveTally.ts`.
   */
  leaveAllowanceDays?: number;
}

// --- Finance (Prompt E) ---------------------------------------------------
// The shared money-loop contract. Each name is defined ONCE here and imported
// by the finance flows (commissionEngine, cashFlow, reconciliationFlow,
// settlementFlow); later prompts import, never redeclare.

/**
 * How an artist is paid out of a day's session takings.
 *  - `percentage`   : artist keeps `artistShare` (0-1) of takings, house keeps the rest.
 *  - `owner-artist` : artist keeps 100% (an owner tattooing their own clients).
 *  - `flat-day-fee` : artist paid a flat amount for the day (typical apprentice deal).
 * Merch never counts toward any of these.
 */
export type CommissionArrangement =
  | { kind: 'percentage'; artistShare: number }
  | { kind: 'owner-artist' }
  | { kind: 'flat-day-fee'; feeAud: number };

/** A single make-sale line contributing to an artist's day (or a merch line). */
export interface SaleLine {
  bookingId?: string;
  amountAud: number;
  /** Merch/aftercare carries no artist commission and is excluded from payout. */
  isMerch?: boolean;
}

/**
 * A make-sale recorded at checkout (E1). The system record E3 reconciles against
 * dockets/settlement. Cash-in on this feeds the day's `CashLedger`; the docket
 * reference is what E3/E4 match on.
 */
export interface SalePayment {
  id: string;
  bookingId: string;
  studioId: StudioId;
  artistId: string;
  customerName: string;
  artistName: string;
  method: 'card' | 'cash' | 'rolled-deposit' | 'mixed';
  /** Session balance taken (excludes merch, excludes deposit already on file). */
  balanceAud: number;
  /** Deposit applied to today's balance (after any manual override). */
  depositAppliedAud: number;
  /** Merch/aftercare taken as its own line — no commission. Total of `merchLines`. */
  merchAud: number;
  /** The individual merch/aftercare items sold, snapshotted at sale time. */
  merchLines: MerchSaleLine[];
  /** Signed reasoned charge adjustment (D3): negative reduces, positive adds. */
  adjustmentAud: number;
  adjustmentReason?: string;
  /** Net cash that hit the till = tendered − change (cash/mixed only). */
  cashInAud: number;
  /**
   * Auto-stamped sale reference (replaces the hand-written docket). Rides the
   * transaction and is echoed at settlement so E4 auto-splits the bank lump.
   */
  saleReference: string;
  takenAt: string; // ISO sim time
  /** True when recorded after the 17:00 cutoff — belongs to tomorrow's lump. */
  afterCutoff: boolean;
}

/**
 * A signed cash-envelope handover (E2), the digital replacement for the paper
 * register — ONE record per artist per WORK day.
 *
 * An artist is never paid in part (client, 30 Jul): a day is handed over whole or
 * it is `deferred` and waits. So one record carries two dates — `dayIso`, the day
 * the work happened ("payment for"), and `paidOnDayIso`, the day the cash actually
 * moved. They differ whenever a short day was carried and settled later, and the
 * days settled together share a `paymentRunId`.
 */
export interface PayoutRecord {
  id: string;
  studioId: StudioId;
  artistId: string;
  /** The WORK day this payout is for (the "payment for" date). */
  dayIso: string;
  arrangement: CommissionArrangement;
  /** Envelope the artist is owed for the day (from the commission engine). */
  amountDueAud: number;
  /**
   * Actually handed over: 0, or exactly `amountDueAud`. NEVER in between — part
   * payment of a day is not a state this system can represent.
   */
  amountPaidAud: number;
  status: 'due' | 'sent' | 'signed' | 'deferred';
  /**
   * The studio day the cash moves (the "paid" date) — stamped when the payment is
   * sent for confirmation and kept on the signed record. Differs from `dayIso`
   * whenever the day was carried and settled later.
   */
  paidOnDayIso?: string;
  /** Shared by every work day handed over in the same payment. */
  paymentRunId?: string;
  /** When the day was deferred, so arrears ageing reads off the record. */
  deferredAt?: string;
  /**
   * Set when the full envelope was only payable by breaking the sealed float
   * (the manager's alternative to deferring).
   */
  shortfall?: 'float-dip';
  signedByName?: string;
  signedAt?: string;
  /** Who authorised a deferral or a float dip (manager). */
  decidedBy?: string;
}

/**
 * A single in/out movement of the drawer float. The float is never overwritten
 * by hand — it moves by infusions (cash added) and withdrawals (cash taken out),
 * each recorded here so the float's in/out is fully auditable. An emergency dip
 * to cover a payout shortfall is one such movement.
 */
export interface FloatMovement {
  id: string;
  at: string; // ISO sim time
  kind: 'opening' | 'infusion' | 'withdrawal' | 'emergency-dip';
  /** Signed: positive adds to the float, negative removes from it. */
  amountAud: number;
  by?: string;
  note?: string;
}

/** Per store, per day float/cash ledger. Powers E1 steering + E3/E4 close. */
export interface CashLedger {
  id: string; // `${studioId}:${dayIso}`
  studioId: StudioId;
  dayIso: string;
  /** Current float balance = running sum of `floatMovements`. Not overwritten
   *  directly; changed only via float movements (infusion / withdrawal / dip). */
  openingFloatAud: number;
  /** Audit trail of every float in/out, oldest first. */
  floatMovements: FloatMovement[];
  /** Cash taken from make-sales so far today (sum of SalePayment.cashInAud). */
  cashInAud: number;
  /** Cash handed out in payouts today (sum of signed PayoutRecord.amountPaidAud). */
  payoutsAud: number;
  /** True once any payout dipped into the float (flagged for E3/E4). */
  floatDipped: boolean;
  /** Counted cash at day close (E4); absent until the day is closed. */
  countedAud?: number;
}

/**
 * A merch / aftercare item a studio sells at checkout (E1). Priced PER STUDIO —
 * each location manages its own list in Studio setup. Retired items stay on
 * historical sales but drop out of the checkout picker.
 */
export interface MerchItem {
  id: string;
  studioId: StudioId;
  name: string;
  category: 'merch' | 'aftercare';
  priceAud: number;
  status: 'active' | 'retired';
}

/** A merch line taken on a sale (a snapshot, so price changes don't rewrite history). */
export interface MerchSaleLine {
  itemId: string;
  name: string;
  priceAud: number;
}

// --- Finance (Prompt E3 — back-office reconciliation) ---------------------

/**
 * A reference-echo line the payment terminal or the deposit sheet produced for a
 * make-sale or a deposit. Back-office reconciliation (E3) matches the system's
 * `SalePayment` records against these by sale reference + amount; the bank
 * settlement split (E4) is formalised later on top of the same echoes.
 *
 * Provider-agnostic by contract — this NEVER names a card provider. A missing
 * `name` is the "docket missing a name" exception E3 surfaces.
 */
export interface DocketLine {
  id: string;
  studioId: StudioId;
  /** The business day (date) this line belongs to. */
  dayIso: string;
  /** Echoes `SalePayment.saleReference` (terminal) or a deposit reference (sheet). */
  reference: string;
  /** Amount the terminal / sheet echoed — matched against the system amount. */
  amountAud: number;
  /** Where the echo came from: a card terminal docket or the deposit sheet. */
  source: 'terminal' | 'deposit-sheet';
  /** Name as it appears on the docket. Absent === a name-missing exception. */
  name?: string;
}

/**
 * One card-takings settlement landing (E3 cash-timing strip): takings taken on a
 * trading day land in the bank a day or two later, and NOTHING lands across the
 * Friday-to-Tuesday weekend gap (the locked settlement reality). Seeded display
 * data for the 7-day strip; E4 builds the full `SettlementLump` split on top.
 */
export interface SettlementLanding {
  id: string;
  studioId: StudioId;
  /** The trading day the card takings were taken (date). */
  paymentDayIso: string;
  /** When the bank lump actually lands (24-36h later; never Fri-Tue). */
  landedOnIso: string;
  amountAud: number;
}

/** How a deposit's money reached (or should reach) the studio bank account. */
export type DepositChannel = 'card' | 'transfer' | 'cash' | 'link';

/**
 * A deposit's money actually landing in the bank — the line the office ticks the
 * deposit spreadsheet against. The deposit-reconciliation lane (amendment 11)
 * matches these to the system's `Deposit` records by reference + amount, so the
 * two failure modes surface: a landing with no `Deposit` is "in the bank, not the
 * system"; a paid `Deposit` with no landing (past the window) is "in the system,
 * not the bank". Provider-agnostic — no card brand appears.
 */
export interface DepositLanding {
  id: string;
  studioId: StudioId;
  /** Echoes `Deposit.depositReference` — what the tie matches on. */
  reference: string;
  amountAud: number;
  channel: DepositChannel;
  /** ISO date the money landed in the bank. */
  landedOn: string;
}

// --- Finance (Prompt E4 — settlement matching + day close) ----------------

/**
 * One line inside a settlement lump: the payment platform echoes back the sale
 * reference it was stamped with, at settlement, so the system can split the bank
 * lump automatically. Provider-agnostic by contract — `terminal` is a neutral
 * terminal/link label, never a card-provider brand.
 */
export interface SettlementLine {
  /** Echoes `SalePayment.saleReference` — what the split matches on. */
  reference: string;
  amountAud: number;
  /** Neutral terminal / link identifier the echo came through (no brand name). */
  terminal: string;
  /** When the original payment was taken. */
  paidAt: string;
}

/**
 * A bank settlement lump: the money actually landing, a day or two after the
 * payments, as a single sum the system splits by stamped reference. The data
 * model is identical whichever provider is used, so this NEVER names one.
 */
export interface SettlementLump {
  id: string;
  studioId: StudioId;
  /** The day the lump landed in the bank. */
  landedOn: string;
  amountAud: number;
  lines: SettlementLine[];
}

/**
 * A frozen end-of-day pack (the "Close the day" output). Once a day is closed the
 * office reads THIS snapshot, not live data, so a later edit can't rewrite a
 * settled day. Reopening is a logged, role-gated action that never silently edits.
 */
export interface DayPack {
  id: string; // `${studioId}:${dayIso}`
  studioId: StudioId;
  dayIso: string;
  closedAt: string;
  closedBy: string;
  /** Cash counted in the till at close. */
  countedAud: number;
  /** Expected in the till from the E2 ledger (float + cash-in − payouts). */
  expectedAud: number;
  /** expected − counted (positive === short, negative === over). */
  varianceAud: number;
  /** Artists whose envelope was still unsigned at close (recorded, not blocking). */
  unsignedArtistIds: string[];
  /**
   * Artists whose envelope the day could not cover, deferred whole at close. A
   * deliberate, logged decision — not the same thing as an unsigned envelope.
   */
  deferredArtistIds: string[];
  /** Frozen snapshots the reconciliation screen reads for a closed day. */
  payments: SalePayment[];
  dockets: DocketLine[];
  cashLedger: CashLedger;
  /** Set when the day was reopened (never deleted — the close is kept for audit). */
  reopenedAt?: string;
  reopenedBy?: string;
  reopenReason?: string;
}

export interface Studio {
  id: StudioId;
  name: string; // location label, e.g. "Coburg"
  brand: string; // brand name, e.g. "Addikted to Ink" or "Melburn Made Ink"
  brandedNumber: string; // AU format
  state: string;
  address: string;
  postcode: string;
  lat: number;
  lon: number;
  artistCount: number;
  assignedAgentIds: string[];
  operatingHours: Record<DayOfWeek, { open: string; close: string } | null>;
  managerId: string;
  isBlackBook: boolean;
  archived?: boolean; // soft-delete flag; absent/false === active
  // Practical info surfaced in confirmation SMS templates + studio screens.
  // All free-text so studio managers can tailor to local conditions.
  gettingHere?: {
    parking?: string;     // "2hr free parking on Reynard St"
    transport?: string;   // "Tram 19 stops outside; train: Coburg station, 4min walk"
    directions?: string;  // optional extra wayfinding cue
  };
  prepNotes?: {
    eatBeforehand?: boolean;  // include "make sure you eat" in long-session prep
    hydrate?: boolean;        // include hydration nudge
    custom?: string;          // free-text per-studio addition
  };
}

export interface Agent {
  id: string;
  firstName: string;
  lastName: string;
  role:
    | 'bookings-agent'
    | 'studio-manager'
    | 'operations-lead'
    | 'owner'
    | 'apprentice'
    | 'artist'
    | 'finance'
    | 'finance-manager';
  assignedStudios: StudioId[];
  primaryStudio?: StudioId;
  avatarUrl?: string;
  shiftHours?: { start: string; end: string };
}

export interface ActivityFeedEntry {
  id: string;
  type:
    | 'inbound-call'
    | 'outbound-call'
    | 'call-completed'
    | 'meta-dm'
    | 'sms-sent'
    | 'sms-received'
    | 'deposit-link-sent'
    | 'deposit-paid'
    // Payment receipt issued to the customer on a successful deposit payment
    // (Diagram 02: the system collects the phone + sends the receipt).
    | 'receipt-sent'
    | 'deposit-expired'
    | 'waiver-sent'
    | 'waiver-signed'
    | 'booking-created'
    | 'booking-cancelled'
    | 'booking-checked-in'
    | 'booking-seated'
    | 'booking-session-noted'
    | 'booking-completed'
    | 'self-book-link-sent'
    | 'self-book-link-used'
    | 'pushed-to-queue'
    | 'cross-studio-route'
    | 'future-readiness-captured'
    | 'future-readiness-fired'
    | 'rebook-attributed'
    | 'rebook-declined'
    | 'rebook-deferred-to-sales'
    | 'payout-signed'
    | 'payout-shortfall'
    | 'float-set'
    | 'day-closed'
    | 'day-reopened'
    | 'voicemail'
    | 'cancellation'
    | 'override-requested'
    | 'override-resolved';
  customerId?: string;
  studioId?: StudioId;
  artistId?: string;
  agentId?: string;
  body: string;
  amount?: number;
  timestamp: string;
}

export interface TailRecord {
  id: string;
  firstName: string;
  lastName: string;
  phone: string;
  suburb: string;
  postcode: string;
  state: string;
  primaryStudioId: StudioId;
  distanceFromNearestKm: number;
  lastInteractionAt: string;
  designType: DesignType;
  lastArtistId?: string;
  lastDepositAud?: number;
  hasUnfinishedPiece: boolean;
  pieceStatus?: Piece['status'];
  doNotContact: boolean;
  engagementStatus: EngagementStatus;
  futureSignal?: boolean;
}

export interface SelfBookLink {
  token: string;
  /**
   * Absent on a universal artist link - that link is customer-agnostic and
   * reusable for anyone; identity is resolved from what the client enters on the
   * page. Present on a staff-sent, named-customer link.
   */
  customerId?: string;
  artistId: string;
  studioId: StudioId;
  /** Pinned length: on staff-sent links, or on a universal link once the artist holds a slot (C1); on an unpinned universal link the client picks it. */
  sessionLength?: SessionLength;
  /** Pinned only on staff-sent links; on a universal link the amount always derives from the chosen session (never pinned, even when a slot is held). */
  depositAud?: number;
  dateRangeStart: string;
  dateRangeEnd: string;
  status: 'active' | 'viewed' | 'paid' | 'expired';
  generatedAt: string;
  expiresAt: string;
  channelSent: Channel;
  bookingId?: string;
  /** True for the artist's one universal, reusable link (customer-agnostic). */
  universal?: boolean;
  /** Pinned slot start (ISO) when the artist has held a specific slot for this link (C1). */
  pinnedStartIso?: string;
  /** The pending deposit currently held for the pinned slot; cleared when paid or re-pinned. */
  pinnedDepositId?: string;
  /**
   * Short human-readable reconciliation code minted at link creation
   * (e.g. AL-ASHER-4F7K), stamped on every Deposit taken through the link so the
   * money reconciles to the right artist. See src/domain/selfBookFlow.ts.
   */
  depositReference: string;
}

/**
 * A marketing spend source (Prompt F). Small seeded fixture so the funnel's
 * "Meta ad spend" line and the per-campaign cost-per-deposit table run on real
 * numbers instead of hardcoded constants. Deposits attribute to a campaign via
 * the customer's `source.campaignId`.
 */
export interface MarketingCampaign {
  id: string;
  name: string;
  channel: 'meta' | 'google' | 'reactivation' | 'referral';
  /** Total ad/outreach spend for the window. */
  spendAud: number;
  /** Outbound messages/impressions worked (the "70 messages, converted 40" audit). */
  messagesSent: number;
  startedAt: string; // ISO
  /** Optional studio targeting; absent = network-wide. */
  studioId?: StudioId;
}

// The confirmation flow is an automated SMS sequence (not an AI voice call).
// Each booking 24h out gets a templated thread that confirms the slot, gives
// getting-here info (from Studio.gettingHere) and prep reminders. "Calls" in
// the type name is historical - the data model represents a confirmation
// thread.
export interface ConfirmationCall {
  id: string;
  bookingId: string;
  customerId: string;
  scheduledFor: string;
  /**
   * Whether this confirmation step is the automated SMS thread (default) or a
   * human call. Lets the cadence surface mix texts and call tasks.
   */
  kind?: 'sms' | 'call';
  recommendedAction: 'auto-confirm' | 'human-preferred' | 'skip';
  approved?: 'approve' | 'exempt' | 'cancel';
  /** Projected total SMS cost for the templated thread (~$0.05-0.12). */
  projectedCostAud: number;
  outcome?: 'confirmed' | 'rescheduled' | 'cancelled' | 'no-answer' | 'question-handoff';
}

export interface InternalCommsMessage {
  id: string;
  authorId: string;
  body: string;
  timestamp: string;
  parentMessageId?: string;
  mentions?: string[];
  reactions?: Array<{ emoji: string; byPersonId: string }>;
  customerRefId?: string;
  autoWinPing?: boolean;
  attachments?: Array<{ kind: 'image' | 'link'; url: string; label?: string }>;
}

export interface InternalCommsChannel {
  id: string;
  name: string;
  studioId?: StudioId;
  scope: 'network' | 'studio-ops' | 'studio-artists' | 'studio-apprentices' | 'managers' | 'owners';
  memberIds: string[];
  description?: string;
  messages: InternalCommsMessage[];
}

export interface InternalDmThread {
  id: string;
  participantIds: string[];
  createdAt: string;
  messages: InternalCommsMessage[];
}
